'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface FAQItemEnhancedProps {
  id: number;
  question: string;
  answer: string;
  category: string;
  relatedLinks?: { text: string; href: string }[];
  kuwaitSpecific?: boolean;
  helpfulVotes: number;
  notHelpfulVotes: number;
  language: 'en' | 'ar';
}

const FAQItemEnhanced = ({
  id,
  question,
  answer,
  category,
  relatedLinks,
  kuwaitSpecific,
  helpfulVotes,
  notHelpfulVotes,
  language
}: FAQItemEnhancedProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [userVote, setUserVote] = useState<'helpful' | 'not-helpful' | null>(null);
  const [localHelpfulVotes, setLocalHelpfulVotes] = useState(helpfulVotes);
  const [localNotHelpfulVotes, setLocalNotHelpfulVotes] = useState(notHelpfulVotes);

  useEffect(() => {
    const savedVote = localStorage.getItem(`faq-vote-${id}`);
    if (savedVote === 'helpful' || savedVote === 'not-helpful') {
      setUserVote(savedVote);
    }

    const savedHelpful = localStorage.getItem(`faq-helpful-${id}`);
    const savedNotHelpful = localStorage.getItem(`faq-not-helpful-${id}`);
    if (savedHelpful) setLocalHelpfulVotes(parseInt(savedHelpful));
    if (savedNotHelpful) setLocalNotHelpfulVotes(parseInt(savedNotHelpful));
  }, [id]);

  const handleVote = (voteType: 'helpful' | 'not-helpful') => {
    if (userVote === voteType) return;

    let newHelpful = localHelpfulVotes;
    let newNotHelpful = localNotHelpfulVotes;

    if (userVote === 'helpful') {
      newHelpful -= 1;
    } else if (userVote === 'not-helpful') {
      newNotHelpful -= 1;
    }

    if (voteType === 'helpful') {
      newHelpful += 1;
    } else {
      newNotHelpful += 1;
    }

    setLocalHelpfulVotes(newHelpful);
    setLocalNotHelpfulVotes(newNotHelpful);
    setUserVote(voteType);

    localStorage.setItem(`faq-vote-${id}`, voteType);
    localStorage.setItem(`faq-helpful-${id}`, newHelpful.toString());
    localStorage.setItem(`faq-not-helpful-${id}`, newNotHelpful.toString());
  };

  return (
    <div className="bg-card rounded-lg shadow-soft overflow-hidden border border-border hover:shadow-elevated transition-shadow duration-base">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-start justify-between p-6 text-left hover:bg-muted transition-colors duration-base"
      >
        <div className="flex-1 pr-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded">
              {category}
            </span>
            {kuwaitSpecific && (
              <span className="text-xs font-semibold text-accent bg-accent/10 px-2 py-1 rounded flex items-center gap-1">
                <Icon name="MapPinIcon" size={12} variant="solid" />
                {language === 'en' ? 'Kuwait Specific' : 'خاص بالكويت'}
              </span>
            )}
          </div>
          <span className="text-lg font-headline font-semibold text-text-primary">
            {question}
          </span>
        </div>
        <Icon
          name="ChevronDownIcon"
          size={24}
          variant="outline"
          className={`text-primary flex-shrink-0 transition-transform duration-base ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="px-6 pb-6 space-y-6 animate-accordion-down">
          <p className="text-text-secondary leading-relaxed">{answer}</p>

          {/* Related Links */}
          {relatedLinks && relatedLinks.length > 0 && (
            <div className="border-t border-border pt-4">
              <p className="text-sm font-semibold text-text-primary mb-3">
                {language === 'en' ? 'Related Links:' : 'روابط ذات صلة:'}
              </p>
              <div className="flex flex-wrap gap-2">
                {relatedLinks.map((link, index) => (
                  <Link
                    key={index}
                    href={link.href}
                    className="inline-flex items-center gap-1 text-sm text-secondary hover:text-primary transition-colors duration-base"
                  >
                    <Icon name="ArrowRightIcon" size={16} variant="outline" />
                    {link.text}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Voting Section */}
          <div className="border-t border-border pt-4">
            <p className="text-sm text-text-secondary mb-3">
              {language === 'en' ? 'Was this answer helpful?' : 'هل كانت هذه الإجابة مفيدة؟'}
            </p>
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleVote('helpful')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-base ${
                  userVote === 'helpful' ?'bg-success text-success-foreground' :'bg-muted text-text-primary hover:bg-success/20'
                }`}
              >
                <Icon name="HandThumbUpIcon" size={20} variant={userVote === 'helpful' ? 'solid' : 'outline'} />
                <span className="font-semibold">{localHelpfulVotes}</span>
                <span className="text-sm">{language === 'en' ? 'Helpful' : 'مفيد'}</span>
              </button>
              <button
                onClick={() => handleVote('not-helpful')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-base ${
                  userVote === 'not-helpful' ?'bg-destructive text-destructive-foreground' :'bg-muted text-text-primary hover:bg-destructive/20'
                }`}
              >
                <Icon name="HandThumbDownIcon" size={20} variant={userVote === 'not-helpful' ? 'solid' : 'outline'} />
                <span className="font-semibold">{localNotHelpfulVotes}</span>
                <span className="text-sm">{language === 'en' ? 'Not Helpful' : 'غير مفيد'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FAQItemEnhanced;